// Consolidation Engine page (consolidation runs + elimination rules)
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Stack, Paper, Grid, Divider, TextField, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton, Tooltip,
    Tabs, Tab,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    SimpleBarChart, StatusPill, fmtMoney, fmtDate, CHART_COLORS,
} from '../agents/AgentUIKit';
import {
    listConsolidationRuns, runConsolidation, getConsolidationRun,
    listEliminationRules, createEliminationRule, deleteEliminationRule,
    listEntities,
} from './api';

const RULE_TYPES = ['IC_RECEIVABLE_PAYABLE', 'IC_REVENUE_COGS', 'IC_INVESTMENT_EQUITY', 'IC_DIVIDEND', 'CUSTOM'];

export default function Consolidation() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [runs, setRuns] = useState([]);
    const [rules, setRules] = useState([]);
    const [entities, setEntities] = useState([]);
    const [currentRun, setCurrentRun] = useState(null);

    // Run dialog
    const [runDialog, setRunDialog] = useState(false);
    const [runForm, setRunForm] = useState({
        period_start: '', period_end: '', reporting_currency: 'USD',
    });

    // Rule dialog
    const [ruleDialog, setRuleDialog] = useState(false);
    const [ruleForm, setRuleForm] = useState({
        name: '', description: '', rule_type: 'IC_RECEIVABLE_PAYABLE',
        from_account_id: '', to_account_id: '',
        entity_from_id: '', entity_to_id: '', is_active: true,
    });

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadAll = async () => {
        const [r1, r2, r3] = await Promise.allSettled([
            listConsolidationRuns(), listEliminationRules(), listEntities(),
        ]);
        if (r1.status === 'fulfilled' && r1.value?.data?.success) setRuns(r1.value.data.data);
        if (r2.status === 'fulfilled' && r2.value?.data?.success) setRules(r2.value.data.data);
        if (r3.status === 'fulfilled' && r3.value?.data?.success) setEntities(r3.value.data.data);
    };

    useEffect(() => { run(loadAll); }, []);  // eslint-disable-line

    const startRun = async () => {
        if (!runForm.period_start || !runForm.period_end) return;
        const r = await run(() => runConsolidation(runForm));
        if (r?.data?.success) {
            setCurrentRun(r.data.data);
            setRunDialog(false);
            await loadAll();
        }
    };

    const viewRun = async (id) => {
        const r = await run(() => getConsolidationRun(id));
        if (r?.data?.success) setCurrentRun(r.data.data);
    };

    const addRule = async () => {
        if (!ruleForm.name) return;
        const r = await run(() => createEliminationRule({
            ...ruleForm,
            from_account_id: ruleForm.from_account_id ? parseInt(ruleForm.from_account_id) : undefined,
            to_account_id: ruleForm.to_account_id ? parseInt(ruleForm.to_account_id) : undefined,
            entity_from_id: ruleForm.entity_from_id || undefined,
            entity_to_id: ruleForm.entity_to_id || undefined,
        }));
        if (r?.data?.success) {
            setRuleDialog(false);
            setRuleForm({ ...ruleForm, name: '', description: '', from_account_id: '', to_account_id: '' });
            await loadAll();
        }
    };

    const delRule = async (id) => {
        if (!window.confirm('Delete this rule?')) return;
        const r = await run(() => deleteEliminationRule(id));
        if (r?.data?.success) await loadAll();
    };

    const exportRun = () => {
        if (!currentRun) return;
        const blob = new Blob([JSON.stringify(currentRun, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `consolidation_run_${currentRun.id}.json`;
        a.click(); URL.revokeObjectURL(url);
    };

    const summaryCards = [
        { label: 'Total Runs', value: runs.length, accent: 'primary' },
        { label: 'Completed', value: runs.filter(r => r.status === 'COMPLETED').length, accent: 'success' },
        { label: 'Elimination Rules', value: rules.length, accent: 'info' },
        { label: 'Active Entities', value: entities.filter(e => e.status === 'ACTIVE').length, accent: 'secondary' },
    ];

    const accountsData = currentRun?.results_json?.accounts || [];
    const topAccountsByMagnitude = [...accountsData]
        .sort((a, b) => Math.abs(b.consolidated_total) - Math.abs(a.consolidated_total))
        .slice(0, 10)
        .map(a => ({ account: a.account_number, consolidated: a.consolidated_total }));

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight={700}>Consolidation</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Consolidate entity balances, apply eliminations, generate consolidated financial statements
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => run(loadAll)}><RefreshIcon /></IconButton>
                </Stack>
                {error && <Typography variant="caption" color="error">{error}</Typography>}

                <SummaryGrid cards={summaryCards} />
                <Divider />

                <Paper variant="outlined" sx={{ p: 0 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                        <Tab label="Consolidation Runs" />
                        <Tab label="Current Run Detail" />
                        <Tab label="Elimination Rules" />
                    </Tabs>
                    <Box sx={{ p: 1.5 }}>
                        {tab === 0 && (
                            <Stack spacing={1}>
                                <SectionHeader
                                    title="Consolidation Runs"
                                    actions={<Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={() => setRunDialog(true)}>New Run</Button>}
                                />
                                {loading && !runs.length ? <LoadingBlock rows={4} /> : (
                                    <DrillTable
                                        columns={[
                                            { key: 'period_start', label: 'Period', render: r => `${fmtDate(r.period_start)} → ${fmtDate(r.period_end)}` },
                                            { key: 'reporting_currency', label: 'Ccy' },
                                            { key: 'entity_count', label: 'Entities', align: 'right' },
                                            { key: 'elimination_count', label: 'Elims', align: 'right' },
                                            { key: 'total_eliminations_amount', label: 'Elim Amt', align: 'right', render: r => fmtMoney(r.total_eliminations_amount) },
                                            { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                            { key: 'created_at', label: 'Created', render: r => r.created_at?.substring(0, 10) },
                                            { key: 'actions', label: '', render: r => <Button size="small" onClick={() => { viewRun(r.id); setTab(1); }}>View</Button> },
                                        ]}
                                        rows={runs}
                                        maxHeight={480}
                                        rowKeyField="id"
                                        onRowClick={(r) => { viewRun(r.id); setTab(1); }}
                                    />
                                )}
                            </Stack>
                        )}
                        {tab === 1 && (
                            currentRun ? (
                                <Stack spacing={1.5}>
                                    <SectionHeader
                                        title={`Run #${currentRun.id}`}
                                        subtitle={`${fmtDate(currentRun.period_start)} → ${fmtDate(currentRun.period_end)} | ${currentRun.reporting_currency}`}
                                        actions={<Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportRun}>Export</Button>}
                                    />
                                    <SummaryGrid cards={[
                                        { label: 'Entities', value: currentRun.entity_count, accent: 'primary' },
                                        { label: 'Eliminations', value: currentRun.elimination_count, accent: 'warning' },
                                        { label: 'Elim Amount', value: fmtMoney(currentRun.total_eliminations_amount), accent: 'info' },
                                        { label: 'Status', value: currentRun.status, accent: currentRun.status === 'COMPLETED' ? 'success' : 'warning' },
                                    ]} />
                                    {topAccountsByMagnitude.length > 0 && (
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" fontWeight={600}>Top 10 Consolidated Balances</Typography>
                                            <SimpleBarChart
                                                data={topAccountsByMagnitude}
                                                xKey="account"
                                                series={[{ key: 'consolidated', label: 'Consolidated', color: CHART_COLORS.primary }]}
                                                height={260}
                                                yFormatter={v => fmtMoney(v)}
                                                showLegend={false}
                                                layout="vertical"
                                            />
                                        </Paper>
                                    )}
                                    {accountsData.length > 0 && (
                                        <DrillTable
                                            columns={[
                                                { key: 'account_number', label: 'Account #' },
                                                { key: 'account_name', label: 'Name' },
                                                { key: 'account_type', label: 'Type', render: r => <Chip size="small" variant="outlined" label={r.account_type} /> },
                                                { key: 'raw_total', label: 'Raw Total', align: 'right', render: r => fmtMoney(r.raw_total) },
                                                { key: 'eliminations', label: 'Elims', align: 'right', render: r => <span style={{ color: CHART_COLORS.danger }}>{fmtMoney(r.eliminations)}</span> },
                                                { key: 'consolidated_total', label: 'Consolidated', align: 'right', render: r => <b>{fmtMoney(r.consolidated_total)}</b> },
                                            ]}
                                            rows={accountsData}
                                            maxHeight={440}
                                        />
                                    )}
                                    {currentRun.error_message && (
                                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(213,43,30,0.05)', borderLeft: '3px solid', borderColor: 'error.main' }}>
                                            <Typography variant="body2" color="error">{currentRun.error_message}</Typography>
                                        </Paper>
                                    )}
                                </Stack>
                            ) : (
                                <EmptyState message="Select a run from the list to see details." />
                            )
                        )}
                        {tab === 2 && (
                            <Stack spacing={1}>
                                <SectionHeader
                                    title="Elimination Rules"
                                    subtitle="Rules to eliminate intercompany balances during consolidation"
                                    actions={<Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setRuleDialog(true)}>New Rule</Button>}
                                />
                                <DrillTable
                                    columns={[
                                        { key: 'name', label: 'Name' },
                                        { key: 'rule_type', label: 'Type', render: r => <Chip size="small" variant="outlined" label={r.rule_type} /> },
                                        { key: 'from_account_id', label: 'From Acct', render: r => r.from_account_id || '—' },
                                        { key: 'to_account_id', label: 'To Acct', render: r => r.to_account_id || '—' },
                                        { key: 'is_active', label: 'Active', render: r => <StatusPill status={r.is_active ? 'active' : 'inactive'} /> },
                                        { key: 'actions', label: '', render: r => (
                                            <Tooltip title="Delete">
                                                <IconButton size="small" color="error" onClick={() => delRule(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                        )},
                                    ]}
                                    rows={rules}
                                />
                            </Stack>
                        )}
                    </Box>
                </Paper>
            </Stack>

            {/* Run Dialog */}
            <Dialog open={runDialog} onClose={() => setRunDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New Consolidation Run</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1} sx={{ pt: 1 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth type="date" label="Period Start" InputLabelProps={{ shrink: true }}
                                value={runForm.period_start} onChange={e => setRunForm({ ...runForm, period_start: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth type="date" label="Period End" InputLabelProps={{ shrink: true }}
                                value={runForm.period_end} onChange={e => setRunForm({ ...runForm, period_end: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Reporting Currency" value={runForm.reporting_currency}
                                onChange={e => setRunForm({ ...runForm, reporting_currency: e.target.value.toUpperCase().slice(0, 3) })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRunDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={startRun} disabled={!runForm.period_start || !runForm.period_end || loading} startIcon={<PlayArrowIcon />}>
                        Run Consolidation
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rule Dialog */}
            <Dialog open={ruleDialog} onClose={() => setRuleDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>New Elimination Rule</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1} sx={{ pt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth label="Name" value={ruleForm.name}
                                onChange={e => setRuleForm({ ...ruleForm, name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth select label="Rule Type" value={ruleForm.rule_type}
                                onChange={e => setRuleForm({ ...ruleForm, rule_type: e.target.value })}>
                                {RULE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth multiline rows={2} label="Description" value={ruleForm.description}
                                onChange={e => setRuleForm({ ...ruleForm, description: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth type="number" label="From Account ID" value={ruleForm.from_account_id}
                                onChange={e => setRuleForm({ ...ruleForm, from_account_id: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth type="number" label="To Account ID" value={ruleForm.to_account_id}
                                onChange={e => setRuleForm({ ...ruleForm, to_account_id: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Entity From (optional)" value={ruleForm.entity_from_id}
                                onChange={e => setRuleForm({ ...ruleForm, entity_from_id: e.target.value })}>
                                <MenuItem value=""><em>Any</em></MenuItem>
                                {entities.map(ent => <MenuItem key={ent.id} value={ent.id}>{ent.entity_code}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Entity To (optional)" value={ruleForm.entity_to_id}
                                onChange={e => setRuleForm({ ...ruleForm, entity_to_id: e.target.value })}>
                                <MenuItem value=""><em>Any</em></MenuItem>
                                {entities.map(ent => <MenuItem key={ent.id} value={ent.id}>{ent.entity_code}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRuleDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={addRule} disabled={!ruleForm.name || loading}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
